import { computed } from 'vue'
import { useSubscriptionStore } from '@/stores/subscription_store'
import { storeToRefs } from 'pinia'
import useFeatureAccess from '@/composables/useFeatureAccess'

export function useSubscriptionAccess() {
  const subscriptionStore = useSubscriptionStore()
  const { schoolSubscription, isLoadingSubscription, hasExpiredTrial } = storeToRefs(subscriptionStore)
  const featureAccess = useFeatureAccess()

  // Subscription status checks
  const hasActiveSubscription = computed(() => {
    return schoolSubscription.value?.status === 'active' ||
           schoolSubscription.value?.is_trial
  })

  const isTrialSubscription = computed(() => {
    return schoolSubscription.value?.is_trial
  })

  const isPaidSubscription = computed(() => {
    return schoolSubscription.value?.status === 'active' && !isTrialSubscription.value
  })

  const hasNoSubscription = computed(() => {
    // Don't report "no subscription" while still loading
    if (isLoadingSubscription.value) return false
    
    return (!schoolSubscription.value?.status ||
           schoolSubscription.value?.status === 'inactive') &&
           !hasExpiredTrial.value
  })

  const isTrialExpired = computed(() => {
    // Check if we have an expired trial from the store
    console.log('hasExpiredTrial', hasExpiredTrial.value, schoolSubscription.value)
    if (hasExpiredTrial.value || schoolSubscription.value?.status === 'expired') {
      return true
    }

    // Also check by date if we have trial subscription with end date
    if (isTrialSubscription.value && schoolSubscription.value?.trial_ends_at) {
      const trialEndDate = new Date(schoolSubscription.value.trial_ends_at)
      const today = new Date()
      return today > trialEndDate
    }

    return false
  })

  // Feature access control
  const canAccessBasicFeatures = computed(() => {
    return hasActiveSubscription.value && !isTrialExpired.value
  })

  const canAccessPremiumFeatures = computed(() => {
    return isPaidSubscription.value
  })

  const canAccessTrialFeatures = computed(() => {
    return isTrialSubscription.value && !isTrialExpired.value
  })

  // Feature-specific access controls
  const canManageStudents = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canManageTeachers = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canManageAttendance = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canManageExams = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canAccessReports = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canAccessAdvancedReports = computed(() => {
    return canAccessPremiumFeatures.value
  })

  const canManageSchoolSettings = computed(() => {
    if (hasNoSubscription.value) return false
    return canAccessBasicFeatures.value
  })

  const canAccessDashboard = computed(() => {
    // Allow basic dashboard access even without subscription but with limited data
    return true
  })

  // Get restriction reason for UI display
  const getRestrictionReason = computed(() => {
    if (isLoadingSubscription.value) {
      return 'Loading subscription...'
    }
    if (hasNoSubscription.value) {
      return 'No active subscription. Please upgrade to access this feature.'
    }
    if (isTrialExpired.value) {
      return 'Trial has expired. Please upgrade to continue using this feature.'
    }
    return null
  })

  // Get the clean plan name for expired trials
  const getExpiredTrialPlanName = computed(() => {
    if (isTrialExpired.value && schoolSubscription.value?.subscription_plan?.name) {
      const fullPlanName = schoolSubscription.value.subscription_plan.name;
      
      // Remove billing cycle information and "Plan" word
      let cleanPlanName = fullPlanName
        .replace(/\s*\([^)]*\)\s*$/g, '') // Remove (Annual), (Monthly), etc.
        .replace(/\s*(Annual|Monthly|Yearly)\s*$/gi, '') // Remove standalone billing terms
        .replace(/\s*Plan\s*$/gi, '') // Remove "Plan" word at the end
        .replace(/\s*Plan\s+/gi, ' ') // Remove "Plan" word in the middle
        .trim();
      
      return cleanPlanName || 'Premium';
    }
    return 'Premium';
  });

  // Get upgrade button text with specific plan name for expired trials
  const getUpgradeButtonText = computed(() => {
    if (isTrialExpired.value) {
      return `Upgrade to ${getExpiredTrialPlanName.value}`;
    }
    if (hasNoSubscription.value) {
      return 'Start Free Trial';
    }
    return 'Upgrade Plan';
  });

  // Get upgrade prompt message
  const getUpgradeMessage = computed(() => {
    if (hasNoSubscription.value) {
      return 'Start your free trial or upgrade to a premium plan to unlock this feature.'
    }
    if (isTrialExpired.value) {
      const planName = getExpiredTrialPlanName.value;
      return `Your ${planName} trial has expired. Upgrade to ${planName} to continue using this feature.`;
    }
    if (isTrialSubscription.value) {
      return 'This feature requires a premium subscription. Upgrade now to unlock advanced features.'
    }
    return 'Upgrade your plan to access this premium feature.'
  })

  // Helper function to check specific feature access
  const canAccessFeature = (feature: string): boolean => {
    // IMPORTANT: Allow access while subscription data is still loading
    // This prevents the race condition where users get redirected to upgrade page
    // before subscription data has been fetched on first login
    if (isLoadingSubscription.value) {
      return true
    }

    // Feature data is fetched by the router before this guard runs. Keep the
    // previous race-condition protection while it is loading, but once loaded
    // never infer entitlement from a legacy label or plan tier.
    if (featureAccess.loading.value || !featureAccess.lastFetched.value) {
      return true
    }

    return featureAccess.hasFeature(feature)
  }

  return {
    // Subscription status
    hasActiveSubscription,
    isTrialSubscription,
    isPaidSubscription,
    hasNoSubscription,
    isTrialExpired,
    hasExpiredTrial,
    isLoadingSubscription,

    // Feature access
    canAccessBasicFeatures,
    canAccessPremiumFeatures,
    canAccessTrialFeatures,
    canManageStudents,
    canManageTeachers,
    canManageAttendance,
    canManageExams,
    canAccessReports,
    canAccessAdvancedReports,
    canManageSchoolSettings,
    canAccessDashboard,

    // Helper functions
    canAccessFeature,
    getRestrictionReason,
    getUpgradeMessage,
    getUpgradeButtonText,
    getExpiredTrialPlanName,

    // Subscription data
    schoolSubscription
  }
}
